/** Roadmap and status report queries. */

import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

export interface RoadmapPeriod {
  key: string
  label: string
  start: string
  end: string
  isCurrent: boolean
}

export interface RoadmapProject {
  id: string
  name: string
  stage: string
  status: string
  ragStatus: string
  workstream: string
  team: string
  teamLabel: string
  teamShortLabel: string
  ownerName: string
  percentComplete: number
  startDate: string | null
  targetDate: string | null
  startIndex: number
  endIndex: number
}

export interface StatusReport {
  generatedAt: string
  periodDays: number
  totals: { projects: number; active: number; atRisk: number; overdue: number; complete: number }
  byRag: Record<string, number>
  byStatus: Record<string, number>
  attention: {
    id: string
    name: string
    ragStatus: string
    ownerName: string
    targetDate: string | null
    percentComplete: number
    overdue: boolean
  }[]
  sprint: {
    name: string
    endDate: string
    daysRemaining: number
    itemCount: number
    committedPoints: number
    donePoints: number
    percentComplete: number
  } | null
  trackers: {
    id: string
    title: string
    total: number | null
    done: number | null
    percent: number | null
    lastSyncedAt: string | null
    lastError: string | null
  }[]
  recentChanges: {
    occurredAt: string
    actor: string
    action: string
    resourceId: string | null
    reason: string | null
  }[]
}

export function useRoadmap(months = 12) {
  return useQuery({
    queryKey: ['reports', 'roadmap', months],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/reports/roadmap', {
        params: { query: { months } },
      })
      return data as unknown as {
        periods: RoadmapPeriod[]
        projects: RoadmapProject[]
        undated: Omit<RoadmapProject, 'startIndex' | 'endIndex'>[]
      }
    },
  })
}

export function useStatusReport(days = 7) {
  return useQuery({
    queryKey: ['reports', 'status', days],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/reports/status', { params: { query: { days } } })
      return data as unknown as StatusReport
    },
  })
}
